/**
 * RiskScheduleLinker — multi-select for linking a RAID Risk to one or more
 * schedule items (milestones / tasks) inside the current project.
 *
 * Uses backend POST /api/projects/{id}/risk-links/suggest to fetch rule-based
 * suggestions (domain→phase mapping + keyword nudges + nearest-upcoming
 * date). User can also toggle any item from the full schedule list.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link2, Milestone, ListChecks, Sparkles, X } from "lucide-react";
import { api } from "../lib/api";

function timeAway(iso) {
  if (!iso) return "no date";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const days = Math.floor((d - new Date()) / 86400000);
  if (days < -1) return `${-days}d overdue`;
  if (days === 0) return "today";
  if (days === 1) return "tomorrow";
  return `${days}d away`;
}

export default function RiskScheduleLinker({ projectId, raid, doc, value, onChange }) {
  const [suggestions, setSuggestions] = useState([]);
  const [busy, setBusy] = useState(false);
  const selected = value || [];
  const debounce = useRef(null);

  // Full schedule list (milestones + tasks) for the "show all" toggle.
  const all = useMemo(() => {
    const items = [];
    (doc?.milestones || []).forEach(m => items.push({
      id: m.id || m.name, name: m.name, type: "milestone", date: m.date, phase: m.phase,
    }));
    (doc?.tasks || []).forEach(t => items.push({
      id: t.id, name: t.name, type: "task", date: t.end, phase: t.phase, critical: t.critical,
    }));
    return items;
  }, [doc]);

  // Fetch fresh suggestions whenever the RAID fields the engine uses change.
  useEffect(() => {
    if (!projectId || !raid) return;
    clearTimeout(debounce.current);
    debounce.current = setTimeout(async () => {
      try {
        setBusy(true);
        const r = await api.riskLinksSuggest(projectId, raid, 6);
        setSuggestions(r.suggestions || []);
      } catch { /* silent */ }
      finally { setBusy(false); }
    }, 250);
    return () => clearTimeout(debounce.current);
  }, [projectId, raid?.title, raid?.desc, raid?.category, raid?.type]);

  const toggle = (id) => {
    if (!id) return;
    const s = new Set(selected);
    s.has(id) ? s.delete(id) : s.add(id);
    onChange(Array.from(s));
  };

  const [showAll, setShowAll] = useState(false);
  const display = showAll ? all : suggestions;
  const selectedItems = all.filter(i => selected.includes(i.id));

  return (
    <div className="rounded-lg border border-zinc-200 bg-zinc-50/60 p-3" data-testid="risk-schedule-linker">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Link2 size={14} className="text-violet-600" />
          <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-600">Linked schedule item(s)</span>
        </div>
        <button
          type="button"
          onClick={() => setShowAll(v => !v)}
          className="text-[11px] font-medium text-violet-700 hover:text-violet-900"
          data-testid="btn-toggle-all-schedule"
        >
          {showAll ? "← Back to suggestions" : "Show full schedule"}
        </button>
      </div>

      {/* Selected chips */}
      {selectedItems.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2" data-testid="linked-items-chips">
          {selectedItems.map(it => (
            <span key={it.id} className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-violet-100 text-violet-800 text-[11px] font-medium"
                  data-testid={`linked-chip-${it.id}`}>
              {it.type === "milestone" ? <Milestone size={10} /> : <ListChecks size={10} />}
              {it.name}
              <button type="button" onClick={() => toggle(it.id)} className="text-violet-600 hover:text-violet-900" data-testid={`btn-unlink-${it.id}`}>
                <X size={11} />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Suggestions / full list */}
      {!showAll && (
        <div className="flex items-center gap-2 mb-1 text-[11px] text-zinc-500">
          <Sparkles size={11} className="text-amber-500" />
          {busy ? "Ranking…" : suggestions.length > 0 ? "Rule-based suggestions (phase + keywords + soonest date)" : "No suggestions yet — set a category or title."}
        </div>
      )}
      <div className="max-h-40 overflow-y-auto divide-y divide-zinc-100 rounded bg-white border border-zinc-100">
        {display.length === 0 && <div className="p-3 text-xs text-zinc-500 text-center">No schedule items.</div>}
        {display.map(it => {
          const chosen = selected.includes(it.id);
          return (
            <label
              key={it.id}
              className={`flex items-start gap-2 px-2.5 py-1.5 text-[12px] cursor-pointer hover:bg-violet-50 ${chosen ? "bg-violet-50/70" : ""}`}
              data-testid={`schedule-option-${it.id}`}
            >
              <input type="checkbox" className="mt-1 accent-violet-600" checked={chosen} onChange={() => toggle(it.id)} data-testid={`schedule-option-checkbox-${it.id}`} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 min-w-0">
                  {it.type === "milestone"
                    ? <Milestone size={11} className="text-blue-600 shrink-0" />
                    : <ListChecks size={11} className="text-emerald-600 shrink-0" />}
                  <span className="font-medium text-zinc-900 truncate">{it.name}</span>
                  {it.critical && <span className="text-[9px] font-mono uppercase tracking-widest text-red-700 bg-red-50 px-1.5 rounded shrink-0">CRIT</span>}
                </div>
                <div className="text-[10px] font-mono text-zinc-500 mt-0.5">
                  {(it.phase || "")}{it.phase ? " · " : ""}{timeAway(it.date)}
                </div>
              </div>
            </label>
          );
        })}
      </div>
    </div>
  );
}
